import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import { useWelcomeAPIInterface } from "@dealer-center/ddc-pages-welcome-fetch";
import { getUrlIncluding, hasPermission } from "./utils/utils";
import { applicationCardData } from "./applicationCardData";
import { CONTAINER_STYLES } from "./styleConstants";
import { SupportContactsSection } from "./components/SupportContactsSection";
import { ApplicationCard } from "./components/ApplicationCard";
import { SectionHeader } from "./components/SectionHeader";
import { Box, Typography } from "@dealer-center/core-components";
import { MessagePage } from "@dealer-center/core-custom-components";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import LABELS from "./labels.json";

interface ApplicationCard {
  title: string;
  description: string;
  icon: string;
  iconColor: string;
  url: string;
  isExternal?: boolean;
  hasSearch?: boolean;
}

export const Welcome = () => {
  const [locale, setLocale] = useState("en-US");
  const [inventorySearch, setInventorySearch] = useState("");
  const { asUser } = useParams<{
    asUser: string;
  }>();
  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
  }>();
  const { data, isLoading, isError, error } = useWelcomeAPIInterface({
    accountId: ccUserInfo?.accountId || "",
    asUserId: asUser || ""
  });
  const navigate = useNavigate();
  const {
    "welcome-page-access": shouldDisplayWelcome,
    "incentives-manager-access": incentivesManagerAccess
  } = useCC9Flags();

  useEffect(() => {
    if (shouldDisplayWelcome === false) {
      navigate("/");
    }
  }, [shouldDisplayWelcome, navigate]);

  Locale.setLabelsOnce(LABELS);

  useEffect(() => {
    document.title = "Welcome :: Dealer.com";
  }, []);

  // Set body background color and hide horizontal overflow
  useEffect(() => {
    const originalBgColor = document.body.style.backgroundColor;
    const originalOverflowX = document.body.style.overflowX;
    document.body.style.backgroundColor = "white";
    document.body.style.overflowX = "hidden";

    return () => {
      document.body.style.backgroundColor = originalBgColor;
      document.body.style.overflowX = originalOverflowX;
    };
  }, []);

  useEffect(() => {
    const newLocale = Locale.getLocale();

    if (locale !== newLocale) {
      Locale.change(newLocale);
      setLocale(newLocale);
    }
  }, [locale]);

  const isRedirecting = shouldDisplayWelcome === false;

  if (isRedirecting) return null;

  // Build application cards dynamically based on available labelLinks
  const applicationCards: ApplicationCard[] = data
    ? (applicationCardData
        .map(config => {
          if (config.fordDirectOnly && !data.isFordDirect) return null;
          if (
            config.titleLabelKey === "INCENTIVES_MANAGER" &&
            incentivesManagerAccess !== true
          )
            return null;
          if (
            config.requiredPermission &&
            !hasPermission(
              {
                userPermissions: ccUserInfo?.userPermissions,
                accountPermissions: ccUserInfo?.accountPermissions
              },
              config.requiredPermission
            )
          )
            return null;
          const rawUrl = config.hrefLabelKey
            ? String(Locale.getText(config.hrefLabelKey))
            : (getUrlIncluding(
                data.labelLinks ?? [],
                config.searchTerms,
                config.excludeSearchTerms
              ) ??
              (config.fallbackSearchTerms
                ? getUrlIncluding(
                    data.labelLinks ?? [],
                    config.fallbackSearchTerms,
                    config.excludeSearchTerms
                  )
                : null));
          if (!rawUrl) return null;
          const transformedUrl = config.transformUrl
            ? config.transformUrl(rawUrl)
            : rawUrl;

          const hashIndex = transformedUrl.indexOf("#");
          const base =
            hashIndex !== -1
              ? transformedUrl.slice(0, hashIndex)
              : transformedUrl;
          const hash = hashIndex !== -1 ? transformedUrl.slice(hashIndex) : "";
          const ccLocale = locale.replace("-", "_");
          const additionalParams: string[] = [];
          if (!config.skipLang) {
            additionalParams.push(`lang=${ccLocale}`);
          }
          if (config.appendParams) {
            additionalParams.push(config.appendParams);
          }
          if (config.appendAccountIdParam) {
            additionalParams.push(
              `${config.appendAccountIdParam}=${ccUserInfo.accountId}`
            );
          }
          const url =
            additionalParams.length > 0
              ? `${base}${base.includes("?") ? "&" : "?"}${additionalParams.join("&")}${hash}`
              : `${base}${hash}`;
          return {
            title: config.titleLabelKey
              ? String(Locale.getText(config.titleLabelKey))
              : config.title,
            description: config.descriptionLabelKey
              ? String(Locale.getText(config.descriptionLabelKey))
              : config.description,
            icon: config.icon,
            iconColor: config.iconColor,
            url,
            isExternal: config.isExternal,
            hasSearch: config.hasSearch
          };
        })
        .filter(card => card !== null) as ApplicationCard[])
    : [];

  if (isLoading) {
    return (
      <MessagePage
        actionName="Loading Welcome page"
        message={String(Locale.getText("LOADING_WELCOME_MSG"))}
      />
    );
  }

  if (isError) {
    const errorMessage =
      (error as Error)?.message?.toLowerCase() === "not found"
        ? String(Locale.getText("ERROR_NOT_FOUND"))
        : String(Locale.getText("ERROR_WELCOME_DATA"));
    return <div>{errorMessage}</div>;
  }

  return (
    <Box
      sx={{
        backgroundColor: "white",
        display: "flex",
        justifyContent: "center"
      }}
    >
      <Box
        className="content-container"
        sx={{
          width: "100%",
          maxWidth: 973,
          backgroundColor: "white"
        }}
      >
        {/* Welcome Header */}
        <Box className="welcome-section">
          <Typography
            sx={{
              fontSize: 40,
              fontWeight: 100,
              lineHeight: "48px",
              color: "#009E77",
              fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif'
            }}
            variant="h1"
          >
            {`${Locale.getText("WELCOME_HEADING")}, ${data?.accountName || "User"}`}
          </Typography>
          {data?.singleViewOfCustomer && (
            <Typography
              sx={{
                fontSize: 12,
                color: "#666",
                marginTop: "4px"
              }}
              variant="body-md"
            >
              Account ID: {data.singleViewOfCustomer}
            </Typography>
          )}
        </Box>

        {/* Applications Section */}
        <Box
          className="application-section"
          sx={{ marginTop: "20px", marginBottom: 6 }}
        >
          <SectionHeader title="Applications" />

          <Box
            className="application-dashboard-list"
            sx={CONTAINER_STYLES.flexWrap}
          >
            {applicationCards.map((card, index) => (
              <ApplicationCard
                description={card.description}
                hasSearch={card.hasSearch}
                icon={card.icon}
                iconColor={card.iconColor}
                isExternal={card.isExternal}
                key={card.title}
                searchValue={inventorySearch}
                title={card.title}
                url={card.url}
                onClick={
                  card.hasSearch
                    ? () => {
                        window.location.href = card.url;
                      }
                    : undefined
                }
                onSearchChange={setInventorySearch}
              />
            ))}
          </Box>
        </Box>

        {/* Support Contacts Section */}
        <SupportContactsSection
          labelLinks={data?.labelLinks}
          singleViewOfCustomer={data?.singleViewOfCustomer}
          supportContacts={data?.supportContacts}
        />
      </Box>
    </Box>
  );
};
